import React, { useEffect, useState } from "react";
import {
  FaGithub,
  FaLinkedin,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { SiGmail, SiX } from "react-icons/si";
import { motion } from "framer-motion";

// ⭐️ Star + Meteor Background
const StarBackground = () => {
  const [stars, setStars] = useState([]);
  const [meteors, setMeteors] = useState([]);
  const [parallaxX, setParallaxX] = useState(0);
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    generateStars();
    generateMeteors();

    const handleResize = () => generateStars();
    const handleMouseMove = (e) => {
      setParallaxX((e.clientX / window.innerWidth - 0.5) * 10);
      setParallaxY((e.clientY / window.innerHeight - 0.5) * 10);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const generateStars = () => {
    const count = Math.floor((window.innerWidth * window.innerHeight) / 8000);
    const newStars = Array.from({ length: count }, (_, i) => ({
      id: i,
      size: Math.random() * 2 + 1,
      x: Math.random() * 100,
      y: Math.random() * 100,
      opacity: Math.random() * 0.6 + 0.3,
      speed: Math.random() * 2 + 1,
    }));
    setStars(newStars);
  };

  const generateMeteors = () => {
    const count = 4;
    const newMeteors = Array.from({ length: count }, (_, i) => ({
      id: i,
      size: Math.random() * 1.5 + 1,
      x: Math.random() * 100,
      y: Math.random() * 20,
      delay: Math.random() * 10,
      duration: Math.random() * 3 + 3,
    }));
    setMeteors(newMeteors);
  };

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {stars.map((star) => (
        <div
          key={star.id}
          style={{
            width: `${star.size}px`,
            height: `${star.size}px`,
            left: `${star.x}%`,
            top: `${star.y}%`,
            opacity: star.opacity,
            position: "absolute",
            borderRadius: "9999px",
            backgroundColor: "white",
            boxShadow: "0 0 15px 3px rgba(255, 255, 255, 0.5)",
            animation: `twinkle ${star.speed}s ease-in-out infinite`,
            transform: `translate(${parallaxX * (star.speed / 2)}px, ${parallaxY * (star.speed / 2)}px)`,
          }}
        />
      ))}
      {meteors.map((meteor) => (
        <div
          key={meteor.id}
          style={{
            width: `${meteor.size * 50}px`,
            height: `${meteor.size * 2}px`,
            left: `${meteor.x}%`,
            top: `${meteor.y}%`,
            animationDelay: `${meteor.delay}s`,
            animationDuration: `${meteor.duration}s`,
            position: "absolute",
            borderRadius: "9999px",
            background: "linear-gradient(to right, white, transparent)",
            boxShadow: "0 0 20px 5px rgba(255, 255, 255, 0.3)",
            transform: "rotate(-45deg)",
            animationName: "meteor",
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
          }}
        />
      ))}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        @keyframes meteor {
          0% { transform: translate(0, 0) rotate(-45deg); opacity: 1; }
          100% { transform: translate(300px, 300px) rotate(-45deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

// ✉️ Contact Component
const Contact = () => {
  return (
    <section className="relative px-6 py-24 text-white min-h-screen overflow-hidden flex items-center justify-center">
      <StarBackground />
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-gray-800 opacity-80 z-0" />

      <div className="relative z-10 max-w-6xl w-full text-center">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl sm:text-5xl font-extrabold mb-6 bg-gradient-to-r from-emerald-400 via-sky-400 to-indigo-500 text-transparent bg-clip-text"
        >
          Get In Touch
        </motion.h2>

        <p className="text-lg text-slate-300 mb-12 max-w-2xl mx-auto">
          🚀 Let’s collaborate on exciting projects. I’m open to freelance and new opportunities!
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full bg-white/5 backdrop-blur-md rounded-xl p-6 border border-sky-500/20 shadow-lg text-left"
          >
            <div className="flex items-center gap-4 mb-4">
              <FaEnvelope className="text-sky-400 text-2xl" />
              <div>
                <p className="text-sm text-slate-400">Email</p>
                <p className="text-white font-medium">hpariya195@gmail.com</p>
              </div>
            </div>

            <div className="flex items-center gap-4 mb-4">
              <FaPhoneAlt className="text-emerald-400 text-2xl" />
              <div>
                <p className="text-sm text-slate-400">Phone</p>
                <p className="text-white font-medium">+91 9601986209</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <FaMapMarkerAlt className="text-indigo-400 text-2xl" />
              <div>
                <p className="text-sm text-slate-400">Location</p>
                <p className="text-white font-medium">Ahmedabad, Gujarat, India</p>
              </div>
            </div>
          </motion.div>

          {/* Availability & Socials */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full bg-white/5 backdrop-blur-md rounded-xl p-6 border border-indigo-500/20 shadow-lg flex flex-col justify-between"
          >
            <div className="mb-6">
              <p className="text-emerald-300 font-semibold mb-1">
                ✅ Available for Freelance
              </p>
              <p className="text-sm text-slate-400">
                Open to new and challenging opportunities
              </p>
            </div>

            <div className="flex justify-center gap-5 text-2xl text-slate-300 mb-6">
              <a
                href="https://github.com/HarshPariya"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-emerald-400 transition"
              >
                <FaGithub />
              </a>
              <a
                href="https://linkedin.com/in/harsh-pariya"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-sky-400 transition"
              >
                <FaLinkedin />
              </a>
              <a
                href="https://twitter.com/HarshPariya"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-400 transition"
              >
                <SiX />
              </a>
              <a
                href="mailto:hpariya195@gmail.com"
                className="hover:text-red-400 transition"
                title="Send Email"
              >
                <SiGmail />
              </a>
            </div>

            <a
              href="mailto:hpariya195@gmail.com"
              className="inline-block bg-gradient-to-r from-emerald-500 via-sky-500 to-indigo-500 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:scale-105 transition-transform"
            >
              📬 Contact Me
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
